import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import '../styles/Drawer.css';

const DrawerContext = createContext(null);

/**
 * Usage anywhere inside the app:
 *
 *   const { openDrawer, closeDrawer } = useDrawer();
 *   openDrawer(<MyComponent onClose={closeDrawer} />);
 *
 * The drawer shell (backdrop + sliding panel) is handled here.
 * Pass any React node as content — it owns its own inner layout.
 */
export function DrawerProvider({ children }) {
  const [content, setContent] = useState(null);

  const openDrawer  = useCallback((node) => setContent(node), []);
  const closeDrawer = useCallback(() => setContent(null), []);

  // Close on Escape key
  useEffect(() => {
    if (!content) return;
    function onKey(e) { if (e.key === 'Escape') closeDrawer(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [content, closeDrawer]);

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer }}>
      {children}
      {content && (
        <>
          <div className="drawer-backdrop" onClick={closeDrawer} />
          <div className="drawer-panel" role="dialog" aria-modal="true">
            {content}
          </div>
        </>
      )}
    </DrawerContext.Provider>
  );
}

export function useDrawer() {
  return useContext(DrawerContext);
}
